/**
 * 어드민 — 포인트 정책 현황 (2026-05 개편)
 *  - 정책 카드 (적립값/일일 상한/만료/최소 보유)
 *  - 오늘 일일 상한 도달 유저
 *  - 만료 예정 요약
 */
import { useQuery } from '@tanstack/react-query';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Badge } from '@/components/atoms/Badge';

interface PolicyResponse {
  policy: Record<string, number>;
  dailyCapExemptActions: string[];
  pointValue: string;
  expireRule: string;
}
interface CapHitResponse {
  cap: number;
  count: number;
  users: { user_id: string; nickname: string; earned_today: number }[];
}
interface ExpirySummary {
  due_30d: number;
  active_total: number;
  expired_total: number;
}

export function PointsPolicyPage() {
  const policyQ = useQuery({
    queryKey: ['admin', 'points', 'policy'],
    queryFn: () => get<PolicyResponse>('/admin/points/policy'),
  });
  const capQ = useQuery({
    queryKey: ['admin', 'points', 'daily-cap-hit'],
    queryFn: () => get<CapHitResponse>('/admin/points/daily-cap-hit'),
  });
  const expQ = useQuery({
    queryKey: ['admin', 'points', 'expiry-summary'],
    queryFn: () => get<ExpirySummary>('/admin/points/expiry-summary'),
  });

  const p = policyQ.data?.policy;

  return (
    <div className="w-full space-y-6">
      <h1 className="text-2xl font-extrabold">포인트 정책 현황</h1>

      <section>
        <h2 className="mb-3 text-lg font-bold">정책 (2026-05 개편)</h2>
        {policyQ.isLoading || !p ? (
          <Skeleton className="h-40" />
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <PolicyCard label="포인트 가치" value="1P = 1원" />
            <PolicyCard label="가입 보너스" value={`${p.signup_bonus}P`} />
            <PolicyCard label="출석체크" value={`${p.checkin}P / 일`} />
            <PolicyCard label="게시글" value={`${p.write_post}P`} sub={`프리미엄 ${p.write_post_premium}P`} />
            <PolicyCard label="댓글" value={`${p.write_comment}P`} sub={`프리미엄 ${p.write_comment_premium}P`} />
            <PolicyCard label="추천인" value={`${p.referral_referrer}P`} sub={`피추천인 ${p.referral_referee}P`} />
            <PolicyCard label="일일 통합 상한" value={`${p.maxDailyPoints}P`} sub="가입·추천 제외" />
            <PolicyCard
              label="교환 최소 보유"
              value={`${p.minExchangeBalance.toLocaleString()}P`}
              sub="이 금액 이상일 때만 신청 가능"
            />
            <PolicyCard label="유효기간" value={`${p.expireMonths}개월`} sub={`${p.expireNotifyDaysBefore}일 전 이메일 안내`} />
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-lg font-bold">
          오늘 일일 상한 도달 유저{' '}
          {capQ.data && (
            <Badge variant="primary">{capQ.data.count}명</Badge>
          )}
        </h2>
        {capQ.isLoading ? (
          <Skeleton className="h-40" />
        ) : !capQ.data || capQ.data.count === 0 ? (
          <div className="rounded-xl border border-[var(--color-border)] p-6 text-center text-sm text-[var(--color-text-sub)]">
            오늘 일일 상한({capQ.data?.cap}P) 에 도달한 유저가 없어요.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
            <table className="w-full min-w-[480px] text-sm">
              <thead className="bg-[var(--color-bg)] text-[var(--color-text-sub)]">
                <tr>
                  <th className="p-3 text-left">닉네임</th>
                  <th className="p-3 text-left">user_id</th>
                  <th className="p-3 text-right">오늘 적립</th>
                </tr>
              </thead>
              <tbody>
                {capQ.data.users.map((u) => (
                  <tr key={u.user_id} className="border-t border-[var(--color-border)]">
                    <td className="p-3 font-semibold">{u.nickname}</td>
                    <td className="p-3 font-mono text-xs text-[var(--color-text-sub)]">{u.user_id}</td>
                    <td className="p-3 text-right tabular-nums">{u.earned_today.toLocaleString()}P</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-lg font-bold">만료 요약</h2>
        {expQ.isLoading || !expQ.data ? (
          <Skeleton className="h-24" />
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <PolicyCard label="30일 내 만료 예정" value={`${expQ.data.due_30d.toLocaleString()}P`} />
            <PolicyCard label="활성 포인트 총합" value={`${expQ.data.active_total.toLocaleString()}P`} />
            <PolicyCard label="누적 소멸" value={`${expQ.data.expired_total.toLocaleString()}P`} />
          </div>
        )}
      </section>
    </div>
  );
}

function PolicyCard(props: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="mb-1 text-xs font-semibold text-[var(--color-text-sub)]">{props.label}</div>
      <div className="text-lg font-extrabold tabular-nums">{props.value}</div>
      {props.sub && <div className="mt-1 text-xs text-[var(--color-text-sub)]">{props.sub}</div>}
    </div>
  );
}
